import type { ForecastQueueRow } from '@/types/backofficeAnalytics.types'

/**
 * The console's rollup pills — the whole book by STATUS (sc-1212).
 *
 * ## What these replaced, and why
 *
 * Four large `StatTile`s counting `ForecastQueueBand`: At risk, Start now,
 * Scheduled, No meeting booked. **The tab restructure made every one of them a
 * restatement of something else on the same screen**, because `bandFor`
 * returns `UNSCHEDULED` for any case with no meeting, so a band maps onto
 * exactly one tab and chip. Measured 2026-10-20:
 *
 * | tile | read | was also |
 * |---|---|---|
 * | At risk | 31 | Scheduled's Overdue 3 + Past safe start 28 |
 * | Scheduled | 27 | Scheduled's On schedule 27 |
 * | Start now | 0 | permanently 0 since `bandFor` took two clocks |
 * | No meeting booked | 93 | the Unscheduled Work tab itself |
 *
 * Tyler: *"i did just notice that the pills dont make sense to our new UI now,
 * expecially no meeting booked thats really unscheduled… Really i think the
 * pills should be the status."*
 *
 * **Status is the one axis the page never showed.** It is orthogonal to the
 * tabs (which split on whether a meeting is booked) and to the group chips
 * (which say what to do next), so it adds a fact instead of echoing one. And
 * it states the firm's position in a single line: on 2026-10-20, **130 cases
 * need starting, 21 are open on someone's desk**.
 *
 * ## The buckets, and who decided them
 *
 * Tyler, 2026-10-20, after seeing the stage counts: *"DG Incomplete… RC Data
 * Entry… Those mean needs started, then add data entry complete to In Progress
 * or Open is a better Label for the rollup."*
 *
 * - **Need started** — the client's data gathering is unfinished, or an analyst
 *   is still entering it into RightCapital. Nobody is building a plan yet.
 * - **Open** — on someone's desk: data entry finished, planning under way, or
 *   back for changes. "Open" rather than "In progress" because Data Entry
 *   Complete is in it and nobody is working that case at that moment.
 * - **Ready** — reached Ready for Presentation. A DIFFERENT POPULATION from the
 *   other two: the queue excludes that stage deliberately (1,235 cases sit
 *   there at a median age of 223 days), so this counts what the three delivered
 *   tabs hold, not the whole stage.
 * - **Behind schedule** — not a status at all, and it CROSS-CUTS the status
 *   buckets (28 of its 37 are in Need started, 9 in Open). It leads the row
 *   because it is the only alarm.
 * - **Start this week** — the leading edge: the safe start has NOT passed but
 *   lands within a week. These are the cases still savable, and they are what
 *   stops tomorrow's Behind schedule from growing. 5 on production.
 *
 * ## Why Behind schedule is not simply "past the required-ready date"
 *
 * It was, and at 5 of 77 it was useless: by the time a plan is past the date it
 * had to be ready, nobody can start it sooner. Tyler, 2026-10-20: *"my end goal
 * is to improve the logic of our KPI rollups to get them aware that i need them
 * starting sooner and finishing sooner to hit the early goal essentially."*
 *
 * So it now also counts cases past their LATEST SAFE START — the date work had
 * to begin to land on past form. That is **read off the `AT_RISK` band rather
 * than recomputed**, which matters twice:
 *
 * - `bandFor` runs two clocks (sc-1212): an unstarted case is graded on its
 *   runway to the safe start, a started one on whether it will still land.
 *   Comparing `safeStartAt` to now directly would mark every started-but-late
 *   case behind even when it is projected to make the date — the false red
 *   removed in PR #1477.
 * - The obvious alternative, `!workStarted`, is a Rothology-shaped signal:
 *   it holds for 1,257 of 1,318 delivered Rothology cases but only **15 of 44**
 *   delivered IE ones, so it would mark the IE book behind for working
 *   differently.
 *
 * Measured on the 77 non-Annual-Review open rows, 2026-10-20: past
 * required-ready alone 5; `AT_RISK` or past required-ready **37**; comparing
 * the safe start directly 54, of which 9 had started and were still on track.
 *
 * Need started and Open partition the open queue exactly — 130 + 21 = 151 — so
 * no open case is missing from the row and none is double-counted.
 */

/**
 * Case Design stage ids, as literals.
 *
 * Hardcoded for the same reason `CASE_DESIGN_STAGES` is in
 * `forecast-stage-pills.ts`: the ids are identical in both HubSpot portals
 * (production 45025262, sandbox 51387696), and the API module that owns them
 * is not reachable from `apps/web`. That module is the fuller vocabulary —
 * every stage with its label and pipeline order — and these two lists are the
 * grouping over it, which `forecast-stage-pills.spec.ts` pins by asserting
 * every stage there resolves to a bucket here.
 */
const NEED_STARTED_STAGE_IDS = [
  '1333293094', // DG Incomplete
  '1058382868', // RC Data Entry
] as const

const OPEN_STAGE_IDS = [
  '1058382869', // Data Entry Complete
  '1058382870', // Planning in Progress
  '1058207543', // Additional Information Needed
  '1123638137', // Revisions Needed
  '1329726795', // Alternative Scenarios Needed
] as const

/** Everything a pill needs off a queue row. */
export type StatusPillRow = Pick<
  ForecastQueueRow,
  'stageId' | 'requiredReadyBy' | 'band' | 'safeStartAt' | 'deadlineAt'
>

export type ForecastStatusPillKey =
  | 'BEHIND_SCHEDULE'
  | 'START_THIS_WEEK'
  | 'DUE_THIS_WEEK'
  | 'NEED_STARTED'
  | 'OPEN'
  | 'READY'

/**
 * How far ahead "Start this week" looks, in CALENDAR days.
 *
 * Calendar, not business days, for two reasons. "This week" is a calendar
 * phrase and a reader will read it as one. And the web has no business-day
 * arithmetic at all -- adding some would be a second implementation of the
 * rule the SERVER used to compute the very `safeStartAt` being compared,
 * free to drift from it. On production the two agree: five business days and
 * seven calendar days select the same 5 cases.
 */
export const START_SOON_WINDOW_DAYS = 7

/**
 * Is `at` inside the next {@link START_SOON_WINDOW_DAYS}, counting from `now`?
 *
 * Both week-shaped cards ask this -- one of a safe start, one of a meeting --
 * so the arithmetic is named once. `setDate` rather than a
 * milliseconds-per-day literal: that number is already written raw in two
 * other files here, and it rolls months and leap years for us.
 */
function withinTheWeek(at: Date, now: Date): boolean {
  // OPEN QUESTION (Tyler, 2026-10-21): a meeting that has already passed is not
  // "due this week", so the Booked with room goal counts it as MET and its
  // shortfall filter omits it. Faithful to this predicate on purpose; whether
  // a past meeting should count against the goal is undecided. Not a live
  // defect: zero past-deadline booked rows on production as of 2026-10-21
  // (`deadlineAt` is always the next upcoming meeting).
  if (at < now) return false
  const cutoff = new Date(now)
  cutoff.setDate(cutoff.getDate() + START_SOON_WINDOW_DAYS)
  return at <= cutoff
}

export interface ForecastStatusPill {
  key: ForecastStatusPillKey
  /** Pill text. Short enough to sit beside a count at phone width. */
  label: string
  /** The tooltip and the Reference tab's bullet. */
  description: string
  /**
   * Whether selecting it can narrow the OPEN queue.
   *
   * False for `READY`, whose rows are not in the queue at all — it narrows the
   * delivered tabs instead. Every pill is clickable (Tyler: *"and each is a
   * clickable filter too!"*); this only says which half of the page a
   * selection reaches, so the empty state can point a reader at the tab that
   * holds their rows rather than leaving them on a blank list.
   */
  narrowsQueue: boolean
}

export const FORECAST_STATUS_PILLS: ReadonlyArray<ForecastStatusPill> = [
  {
    key: 'BEHIND_SCHEDULE',
    label: 'Behind schedule',
    description:
      'Work should already have started to land on past form, or the plan is past the date it had to be ready.',
    narrowsQueue: true,
  },
  {
    key: 'START_THIS_WEEK',
    label: 'Start Now to Catch Up',
    description:
      'The latest safe start lands within a week. Still savable, and what keeps Behind schedule from growing.',
    narrowsQueue: true,
  },
  {
    key: 'DUE_THIS_WEEK',
    label: 'Due this week',
    description:
      'The client meeting is within a week and nobody has started. Carved out of Up Next, because a case that close to its meeting is not "next", it is now.',
    narrowsQueue: true,
  },
  {
    key: 'NEED_STARTED',
    label: 'Up Next',
    description:
      'Data gathering is unfinished, or it is still being entered into RightCapital. No plan is being built yet.',
    narrowsQueue: true,
  },
  {
    key: 'OPEN',
    label: 'Open',
    description:
      'On someone’s desk: data entry done, planning under way, or back for changes.',
    narrowsQueue: true,
  },
  {
    key: 'READY',
    label: 'Queue',
    description:
      'Reached Ready for Presentation. Counts what the delivered tabs hold, not the whole stage.',
    narrowsQueue: false,
  },
]

/**
 * Which status an open queue row is in, or null when its stage is in neither
 * bucket.
 *
 * **Null should never happen and is not defended against elsewhere** — the two
 * id lists above cover every stage in the API's `OPEN_CASE_DESIGN_STAGE_IDS`,
 * which is what the queue selects on. It is returned rather than thrown so a
 * stage added to that set upstream shows up as a row missing from the pills
 * instead of a blank page, and `statusPillCounts` asserts the partition in its
 * own spec.
 */
export function openStatusFor(
  row: Pick<ForecastQueueRow, 'stageId' | 'deadlineAt'>,
  now: Date,
): Extract<
  ForecastStatusPillKey,
  'DUE_THIS_WEEK' | 'NEED_STARTED' | 'OPEN'
> | null {
  const id: string = row.stageId
  if ((NEED_STARTED_STAGE_IDS as ReadonlyArray<string>).includes(id)) {
    // Carved out of Up Next rather than cross-cutting it (Tyler, 2026-10-20:
    // "pull out any from need started that have a deadline of this week into
    // that block"). A case whose meeting is days away and which nobody has
    // started is not "next", it is now -- and leaving it inside a 55-row pile
    // is how it gets missed. Both production rows sit in DG Incomplete, so the
    // client has not even finished data gathering.
    return isDueThisWeek(row, now) ? 'DUE_THIS_WEEK' : 'NEED_STARTED'
  }
  if ((OPEN_STAGE_IDS as ReadonlyArray<string>).includes(id)) return 'OPEN'
  return null
}

/**
 * Is the delivery meeting inside the next week?
 *
 * Reads the booked MEETING, not the required-ready date: the card answers "the
 * client is sitting down imminently", which is the fact that makes an unstarted
 * case alarming. Calendar days, for the same reason
 * {@link START_SOON_WINDOW_DAYS} is -- see its docblock.
 *
 * A case whose meeting has already passed is not "due this week"; it is behind,
 * and {@link isBehindSchedule} has it.
 */
export function isDueThisWeek(
  row: Pick<ForecastQueueRow, 'deadlineAt'>,
  now: Date,
): boolean {
  if (!row.deadlineAt) return false
  return withinTheWeek(new Date(row.deadlineAt), now)
}

/** Is this row past the date its plan had to be ready? */
export function isPastRequiredReady(
  row: Pick<ForecastQueueRow, 'requiredReadyBy'>,
  now: Date,
): boolean {
  return row.requiredReadyBy !== null && new Date(row.requiredReadyBy) < now
}

/**
 * Should this case already have started, or already be ready?
 *
 * `AT_RISK` carries the first half and is deliberately NOT recomputed here --
 * see the module docblock for the two traps that avoids.
 */
export function isBehindSchedule(
  row: Pick<ForecastQueueRow, 'requiredReadyBy' | 'band'>,
  now: Date,
): boolean {
  return row.band === 'AT_RISK' || isPastRequiredReady(row, now)
}

/**
 * Is the safe start still ahead, but inside {@link START_SOON_WINDOW_DAYS}?
 *
 * Mutually exclusive with {@link isBehindSchedule} by construction: a case
 * already past its safe start is behind, not starting. A null `safeStartAt`
 * -- no meeting, or no estimate for this work -- is not "soon", it is
 * unknown, and 15 of the 77 open rows have no deadline to work back from.
 */
export function isStartingThisWeek(
  row: Pick<ForecastQueueRow, 'requiredReadyBy' | 'band' | 'safeStartAt'>,
  now: Date,
): boolean {
  if (!row.safeStartAt || isBehindSchedule(row, now)) return false
  return withinTheWeek(new Date(row.safeStartAt), now)
}

/**
 * Does `row` match a pill? `PAST_DUE` cross-cuts the status buckets, so a row
 * can match two selected pills — the selection is a union, like every other
 * multi-select on this page.
 */
export function matchesStatusPill(
  row: StatusPillRow,
  key: ForecastStatusPillKey,
  now: Date,
): boolean {
  if (key === 'BEHIND_SCHEDULE') return isBehindSchedule(row, now)
  if (key === 'START_THIS_WEEK') return isStartingThisWeek(row, now)
  if (key === 'READY') return false
  return openStatusFor(row, now) === key
}

/**
 * The numbers on the pills.
 *
 * `readyCount` is passed in rather than derived: those rows live in the
 * delivered sections, which the queue does not contain.
 *
 * Counted from the rows that have passed every OTHER filter but not the pill
 * selection, so a pill reads the same whether or not it is selected — the rule
 * `groupCounts` and `CleaningKindFilterChips` already follow.
 */
export function statusPillCounts(
  rows: ReadonlyArray<StatusPillRow>,
  readyCount: number,
  now: Date,
): Record<ForecastStatusPillKey, number> {
  const counts: Record<ForecastStatusPillKey, number> = {
    BEHIND_SCHEDULE: 0,
    START_THIS_WEEK: 0,
    DUE_THIS_WEEK: 0,
    NEED_STARTED: 0,
    OPEN: 0,
    READY: readyCount,
  }
  for (const row of rows) {
    if (isBehindSchedule(row, now)) counts.BEHIND_SCHEDULE += 1
    if (isStartingThisWeek(row, now)) counts.START_THIS_WEEK += 1
    const status = openStatusFor(row, now)
    if (status) counts[status] += 1
  }
  return counts
}
